// ────────────────────────────────────────────────────────────
// DEALS — the Deal Desk index (stage='titled', newest first)
// ────────────────────────────────────────────────────────────
// Deals appear because the title agent wrote their chain — there is
// no manual creation or import here. Rows are live from D1.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { DealListRow } from './types';
import { getDealSource } from './data/source';
import { TitleRunStrip } from './components/TitleRunStrip';

export function DealsList() {
  const [deals, setDeals] = useState<DealListRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    getDealSource().listTitledDeals()
      .then(d => { if (alive) setDeals(d); })
      .catch(e => { if (alive) setErr((e as Error).message); });
    return () => { alive = false; };
  }, []);

  return (
    <div className="dd-page dd-tracts-page">
      <div className="dd-tracts-head">
        <h1 className="dd-owner-name">Deals</h1>
        <span className="dd-card-meta">titled by the research agent · newest first</span>
      </div>

      <TitleRunStrip scope={{ kind: 'all' }} />

      {err && <div className="dd-error"><strong>Could not load deals</strong>{err}</div>}
      {!err && deals === null && <div className="dd-loading">Loading deals…</div>}
      {deals !== null && deals.length === 0 && (
        <div className="dd-loading">Deals appear here when title research completes.</div>
      )}

      <div className="dd-tract-rows">
        {(deals ?? []).map(d => (
          <Link key={d.id} to={`/deal-desk/deals/${d.id}`} className="dd-tract-row dd-deal-row">
            <span className="who">{d.subjectParty}</span>
            <span className="trs">{d.trs}</span>
            <span className="county">{d.county} County</span>
            <span className="meta">{d.nmaEstimate != null ? `${d.nmaEstimate} NMA` : '— NMA'}</span>
            <span className="meta">titled {fmtDate(d.createdAt)}</span>
            <span className="meta status">{d.status}</span>
            <span className={`conf ${d.confidence == null ? '' : d.confidence >= 70 ? 'high' : d.confidence >= 45 ? 'mid' : 'low'}`}>
              {d.confidence != null ? `${d.confidence}%` : '—'}
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}
